class LandingPageStickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.maxQuantity = 10;

    this.onAddToCartClick = this.addToCartEventHandler.bind(this);
    this.onIntersectionChange = this.handleIntersectionChange.bind(this);
    this.onMediaQueryChange = this.handleMediaQueryChange.bind(this);
  }

  // Called when the element is connected to the DOM.
  connectedCallback() {
    this.section = this;
    this.hostSection =
      this.closest(".landing-page--sticky-add-to-cart") || this;

    this.addToCartBtn = this.section.querySelector("#sticky-add-to-cart-btn");

    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener("click", this.onAddToCartClick);
    }

    this.setStickyVisibility(false);
    this.initVisibilityControls();
  }

  // Called when the element is disconnected from the DOM.
  disconnectedCallback() {
    if (this.addToCartBtn) {
      this.addToCartBtn.removeEventListener("click", this.onAddToCartClick);
    }

    this.teardownVisibilityControls();
  }

  addToCartEventHandler(event) {
    const btn = event.target.closest("#sticky-add-to-cart-btn");
    if (!btn) return;
    if (btn.disabled || btn.getAttribute("aria-disabled") === "true") return;
    const { variantId } = btn.dataset;
    this.addToCart(variantId);
  }

  /**
   * @param {string} id - The ID of the item to add.
   */
  async addToCart(id) {
    const quantity = 1;

    const formData = {
      items: [
        {
          id,
          quantity,
        },
      ],
    };

    try {
      await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
    } catch (error) {
      console.log(`❌ LP Sticky Add to Cart | addToCart | ${error}`);
    }
  }

  initVisibilityControls() {
    if (!this.hostSection) return;

    if (!window.matchMedia) {
      this.setStickyVisibility(true);
      return;
    }

    this.mobileMediaQuery = window.matchMedia("(max-width: 3068px)");

    if (this.mobileMediaQuery.addEventListener) {
      this.mobileMediaQuery.addEventListener("change", this.onMediaQueryChange);
    } else if (this.mobileMediaQuery.addListener) {
      this.mobileMediaQuery.addListener(this.onMediaQueryChange);
    }

    this.handleMediaQueryChange(this.mobileMediaQuery.matches);
  }

  teardownVisibilityControls() {
    this.detachIntersectionObserver();

    if (this.mobileMediaQuery) {
      if (this.mobileMediaQuery.removeEventListener) {
        this.mobileMediaQuery.removeEventListener(
          "change",
          this.onMediaQueryChange
        );
      } else if (this.mobileMediaQuery.removeListener) {
        this.mobileMediaQuery.removeListener(this.onMediaQueryChange);
      }
    }

    this.mobileMediaQuery = undefined;
  }

  handleMediaQueryChange(eventOrMatches) {
    const matches =
      typeof eventOrMatches === "boolean"
        ? eventOrMatches
        : Boolean(eventOrMatches?.matches);

    if (matches) {
      this.attachIntersectionObserver();
    } else {
      this.detachIntersectionObserver();
      this.setStickyVisibility(true);
    }
  }

  attachIntersectionObserver() {
    if (this.intersectionObserver || !("IntersectionObserver" in window)) {
      if (!("IntersectionObserver" in window)) {
        this.setStickyVisibility(true);
      }
      return;
    }

    this.visibilityTarget =
      document.querySelector("#select-edition-select_edition_DeDYTV") ||
      document.querySelector(".custom-product-info-select-edition");

    if (!this.visibilityTarget) {
      this.setStickyVisibility(true);
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      this.onIntersectionChange,
      {
        threshold: 0.1,
      }
    );

    this.intersectionObserver.observe(this.visibilityTarget);
    this.updateStickyVisibilityFromTarget();
  }

  detachIntersectionObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    this.intersectionObserver = undefined;
    this.visibilityTarget = undefined;
  }

  handleIntersectionChange(entries) {
    if (!Array.isArray(entries)) return;

    const entry = entries.find((item) => item.target === this.visibilityTarget);

    if (!entry) return;

    this.setStickyVisibility(
      !(entry.isIntersecting && entry.intersectionRatio > 0)
    );
  }

  updateStickyVisibilityFromTarget() {
    if (!this.visibilityTarget) {
      this.setStickyVisibility(true);
      return;
    }

    const rect = this.visibilityTarget.getBoundingClientRect();
    const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;

    this.setStickyVisibility(!isVisible);
  }

  setStickyVisibility(shouldShow) {
    if (!this.hostSection) return;

    if (this.isStickyVisible === shouldShow) return;

    this.isStickyVisible = shouldShow;
    const visibilityValue = shouldShow ? "true" : "false";

    this.hostSection.setAttribute("data-sticky-visible", visibilityValue);
    this.hostSection.setAttribute("aria-hidden", shouldShow ? "false" : "true");

    this.setAttribute("data-sticky-visible", visibilityValue);
    this.setAttribute("aria-hidden", shouldShow ? "false" : "true");
  }
}

customElements.define(
  "landing-page-sticky-add-to-cart",
  LandingPageStickyAddToCart
);

document.addEventListener("DOMContentLoaded", function () {
  const stickyMenu = document.querySelector(".landing-page--sticky-add-to-cart");
  if (!stickyMenu) return;

  const stickyEditionOptions = Array.from(
    stickyMenu.querySelectorAll(".edition-selector .edition-option")
  );
  const stickyButton = stickyMenu.querySelector("#sticky-add-to-cart-btn");
  const stickySoldOutText = stickyMenu.querySelector("#sticky-sold-out-text");

  function extractPriceLabelFromText(text) {
    if (!text) return "";
    const match = String(text).match(/(\$\s?[\d,]+(?:\.\d{1,2})?)/);
    return match ? match[1].replace(/\s+/g, "") : "";
  }

  function formatSoldOutLabel(priceLabel) {
    return priceLabel ? `SOLD OUT - ${priceLabel}` : "SOLD OUT";
  }

  function setStickySoldOutState(isSoldOut, priceLabel = "") {
    if (!stickyButton) return;

    if (isSoldOut) {
      stickyButton.disabled = true;
      stickyButton.setAttribute("aria-disabled", "true");
      stickyButton.classList.add("hide");

      if (stickySoldOutText) {
        stickySoldOutText.textContent = formatSoldOutLabel(priceLabel);
        stickySoldOutText.classList.add("show");
      }

      return;
    }

    stickyButton.disabled = false;
    stickyButton.setAttribute("aria-disabled", "false");
    stickyButton.classList.remove("hide");

    if (stickySoldOutText) {
      stickySoldOutText.classList.remove("show");
    }
  }

  function getPdpEditionOptions() {
    return Array.from(document.querySelectorAll(".edition-option")).filter(
      (option) => !stickyMenu.contains(option)
    );
  }

  function getCurrentPdpButton() {
    const buttons = Array.from(
      document.querySelectorAll("[id^='add-to-cart-btn'], #add-to-cart-btn")
    ).filter((button) => !stickyMenu.contains(button));

    return buttons.find((button) => button.offsetParent !== null) || buttons[0] || null;
  }

  function syncStickyButtonTextFromPdp() {
    if (!stickyButton) return;

    const pdpButton = getCurrentPdpButton();
    const stickyButtonText = stickyButton.querySelector(".button-text");
    const pdpButtonText = pdpButton?.querySelector(".button-text");
    const soldOutElement = document.querySelector(".sold-out-text.show");
    const soldOutText = soldOutElement?.textContent?.trim() || "";
    const priceFromSoldOut = extractPriceLabelFromText(soldOutText);
    const priceFromPdpButton = extractPriceLabelFromText(
      pdpButtonText?.textContent || ""
    );
    const isSoldOut = Boolean(
      pdpButton?.disabled || (soldOutElement && soldOutElement.offsetParent !== null)
    );

    if (isSoldOut) {
      setStickySoldOutState(true, priceFromSoldOut || priceFromPdpButton);
      return;
    }

    setStickySoldOutState(false);

    if (stickyButtonText && pdpButtonText && pdpButtonText.innerHTML.trim() !== "") {
      stickyButtonText.innerHTML = pdpButtonText.innerHTML.trim();
    }
  }

  function selectStickyOption(option) {
    if (!option) return;

    stickyEditionOptions.forEach((opt) => opt.classList.remove("selected"));
    option.classList.add("selected");

    stickyMenu
      .querySelectorAll(".edition-radio")
      .forEach((radio) => radio.classList.remove("selected"));

    const currentRadio = option.querySelector(".edition-radio");
    if (currentRadio) {
      currentRadio.classList.add("selected");
    }
  }

  function findStickyOptionByHandle(handle) {
    if (!handle) return null;

    return (
      stickyEditionOptions.find((option) => option.dataset.edition === handle) ||
      stickyEditionOptions.find((option) => option.dataset.normalProduct === handle) ||
      stickyEditionOptions.find((option) => option.dataset.veganProduct === handle) ||
      null
    );
  }

  function findPdpOptionByHandle(handle) {
    if (!handle) return null;

    const pdpOptions = getPdpEditionOptions();

    return (
      pdpOptions.find((option) => option.dataset.edition === handle) ||
      pdpOptions.find((option) => option.dataset.normalProduct === handle) ||
      pdpOptions.find((option) => option.dataset.veganProduct === handle) ||
      null
    );
  }

  function updateStickyVariantAndPriceFromHandle(handle) {
    if (!stickyButton || !handle) return;

    fetch(`/products/${handle}.js`)
      .then((response) => response.json())
      .then((product) => {
        const selectedOrFirstVariant =
          product.variants.find((variant) => variant.available) || product.variants[0];
        const stickyButtonText = stickyButton.querySelector(".button-text");

        if (selectedOrFirstVariant) {
          stickyButton.dataset.variantId = selectedOrFirstVariant.id;
          if (!selectedOrFirstVariant.available) {
            const soldOutPrice = `$${(selectedOrFirstVariant.price / 100).toFixed(0)}`;
            setStickySoldOutState(true, soldOutPrice);
          } else {
            setStickySoldOutState(false);
            if (stickyButtonText) {
              stickyButtonText.textContent = `ADD TO CART - $${(
                selectedOrFirstVariant.price / 100
              ).toFixed(0)}`;
            }
          }
        }
      })
      .catch((error) => {
        console.error("Sticky ATC | product fetch failed:", error);
      })
      .finally(() => {
        // Keep button copy in sync with PDP logic (subscriptions, discounts, etc.)
        syncStickyButtonTextFromPdp();
      });
  }

  function dispatchStickySelectionEvent(option, originalEvent) {
    const event = new CustomEvent("resetSelected-StickyMenu", {
      detail: {
        option: option.dataset,
        originalEvent,
      },
      bubbles: true,
      composed: true,
    });
    stickyMenu.dispatchEvent(event);
  }

  // Sticky -> PDP sync
  stickyEditionOptions.forEach((option) => {
    option.addEventListener("click", function (event) {
      event.stopPropagation();

      selectStickyOption(this);
      dispatchStickySelectionEvent(this, event);

      const handle = this.dataset.edition || this.dataset.normalProduct;
      updateStickyVariantAndPriceFromHandle(handle);

      const pdpOption = findPdpOptionByHandle(handle);
      if (pdpOption) {
        pdpOption.click();
      }
    });
  });

  // PDP custom event -> Sticky sync (used by reset flow)
  document.addEventListener("resetSelected-PDP", (event) => {
    const handle = event?.detail?.edition || event?.detail?.option?.edition;
    const stickyOption = findStickyOptionByHandle(handle);
    if (!stickyOption) return;

    selectStickyOption(stickyOption);
    updateStickyVariantAndPriceFromHandle(
      stickyOption.dataset.edition || stickyOption.dataset.normalProduct
    );
  });

  // PDP click fallback -> Sticky sync (covers essentials and other selectors)
  document.addEventListener("click", (event) => {
    const clickedOption = event.target.closest(".edition-option");
    if (!clickedOption || stickyMenu.contains(clickedOption)) return;

    const handle = clickedOption.dataset.edition || clickedOption.dataset.normalProduct;
    const stickyOption = findStickyOptionByHandle(handle);
    if (!stickyOption) return;

    // Defer to let PDP scripts finish their own selection/price updates first
    setTimeout(() => {
      selectStickyOption(stickyOption);
      updateStickyVariantAndPriceFromHandle(
        stickyOption.dataset.edition || stickyOption.dataset.normalProduct
      );
    }, 0);
  });

  // Keep sticky label synced if PDP scripts update button text asynchronously
  const pdpButtonObserver = new MutationObserver(() => {
    syncStickyButtonTextFromPdp();
  });

  const pdpButton = getCurrentPdpButton();
  if (pdpButton) {
    pdpButtonObserver.observe(pdpButton, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
    });
  }

  // Initial sync
  const initiallySelectedPdpOption = getPdpEditionOptions().find((option) =>
    option.classList.contains("selected")
  );

  const initialHandle =
    initiallySelectedPdpOption?.dataset.edition ||
    initiallySelectedPdpOption?.dataset.normalProduct ||
    stickyEditionOptions.find((option) => option.classList.contains("selected"))?.dataset
      .edition ||
    stickyEditionOptions[0]?.dataset.edition ||
    stickyEditionOptions[0]?.dataset.normalProduct;

  const initialStickyOption = findStickyOptionByHandle(initialHandle);
  if (initialStickyOption) {
    selectStickyOption(initialStickyOption);
  }

  updateStickyVariantAndPriceFromHandle(initialHandle);
});
